/**
 * Lazy loaded components
 *
 * We want to minimized the size of the JS delivered to the client
 * Every component that:
 * 1) has a substantial set of conditional child components
 * 2) it's very big (should implement point 1)
 *
 * will need to use dynamic imports
 * If the sub components is shared among other components then it needs to
 * be put here and imported
 */

// Pages
export const HomePage = () => import(/* webpackChunkName: "home" */ '@/pages/home/HomePage.vue');
export const New = () => import(/* webpackChunkName: "new" */ '@/pages/New.vue');
export const Upcoming = () => import(/* webpackChunkName: "upcoming" */ '@/pages/Upcoming.vue');
export const PlotRecommender = () =>
	import(/* webpackChunkName: "lp-plot-recommender" */ '@/pages/landing-pages/plot-recommender/PlotRecommender.vue');
export const Popular = () => import(/* webpackChunkName: "popular" */ '@/pages/Popular.vue');
export const RefreshToken = () => import(/* webpackChunkName: "refresh-token" */ '@/pages/RefreshToken.vue');
export const Search = () => import(/* webpackChunkName: "search" */ '@/pages/Search.vue');
export const StaticPage = () => import(/* webpackChunkName: "static" */ '@/pages/StaticPage.vue');
export const StreamGreen = () =>
	import(/* webpackChunkName: "lp-streamgreen" */ '@/pages/landing-pages/stream-green/StreamGreen.vue');
export const SubscriberForm = () =>
	import(/* webpackChunkName: "lp-subscriber-form" */ '@/pages/landing-pages/subscriber-form/SubscriberForm.vue');
export const Talent = () => import(/* webpackChunkName: "talent" */ '@/pages/Talent.vue');
export const TitleDetail = () => import(/* webpackChunkName: "detail" */ '@/pages/TitleDetail.vue');
export const TvRegistration = () => import(/* webpackChunkName: "tv-registration" */ '@/pages/TvRegistration.vue');
export const Watchlist = () => import(/* webpackChunkName: "watchlist" */ '@/pages/Watchlist.vue');
export const CollegesMostSuperBowlPlayers = () =>
	import(
		/* webpackChunkName: "collegesmostsuperbowlplayers" */ '@/pages/landing-pages/sports/CollegesMostSuperBowlPlayers.vue'
	);
export const ExpansionSeasonsMLS = () =>
	import(/* webpackChunkName: "expansionseasonmls" */ '@/pages/landing-pages/sports/ExpansionSeasonsMLS.vue');
export const NBAPlayoffs = () =>
	import(/* webpackChunkName: "nbaplayoffs" */ '@/pages/landing-pages/sports/NBAPlayoffs.vue');
export const StreamingCharts = () =>
	import(/* webpackChunkName: "streaming-charts" */ '@/pages/landing-pages/streaming-charts/StreamingCharts.vue');
export const StatisticPage = () =>
	import(/* webpackChunkName: "statistics-page" */ '@/pages/landing-pages/statistics/StatisticPage.vue');
export const GlobalPress = () =>
	import(/* webpackChunkName: "global-press" */ '@/pages/landing-pages/global-press/GlobalPress.vue');
export const APNews = () => import(/* webpackChunkName: "ap-news" */ '@/pages/landing-pages/ap-news/APNews.vue');

// Title List
export const TitleList = () => import(/* webpackChunkName: "title-list" */ '@/components/list/TitleList.vue');

// Modals
export const AccountModal = () =>
	import(/* webpackChunkName: "account-modal" */ '@/components/modals/account/AccountModal.vue');
export const LoginManager = () =>
	import(/* webpackChunkName: "login-manager-modal" */ '@/components/login-manager/LoginManager.vue');
export const CountrySelectionModal = () =>
	import(/* webpackChunkName: "country-selection-modal" */ '@/components/mobile/CountrySelectionModal.vue');
export const ProModal = () => import(/* webpackChunkName: "pro-modal" */ '@/components/modals/pro/ProModal.vue');

export const CancelProModal = () =>
	import(/* webpackChunkName: "cancel-pro-modal" */ '@/components/modals/pro/CancelProModal.vue');
export const LanguageSelectionModal = () =>
	import(/* webpackChunkName: "language-selection-modal" */ '@/components/mobile/LanguageSelectionModal.vue');
export const LoginMessageModal = () =>
	import(/* webpackChunkName: "login-message-modal" */ '@/components/modals/login/LoginMessageModal.vue');
export const PackagesSelectionModal = () =>
	import(
		/* webpackChunkName: "provider-selection-modal" */ '@/components/modals/package-selection/PackagesSelectionModal.vue'
	);
export const FilterProductionCountryMoreModal = () =>
	import('@/components/modals/FilterProductionCountryMoreModal.vue');

export const SettingsModal = () =>
	import(/* webpackChunkName: "settings-modal" */ '@/components/modals/SettingsModal.vue');
export const CountryList = () =>
	import(/* webpackChunkName: "country-list" */ '@/components/modals/country-list/CountryList.vue');
export const LanguageList = () =>
	import(/* webpackChunkName: "language-list" */ '@/components/modals/account/language-list/LanguageList.vue');
export const TextRecommendationModalV2 = () =>
	import(
		/* webpackChunkName: "text-recommendation-funnel-modal" */ '@/features/why-to-watch/components/TextRecommendationModalV2.vue'
	);
export const TextRecommendationCardTitleDetailModal = () =>
	import(
		/* webpackChunkName: "text-recommendation-modal" */ '@/features/why-to-watch/components/TextRecommendationCardTitleDetailModal.vue'
	);

// Components
export const FilterBar = () => import(/* webpackChunkName: "filter-bar" */ '@/components/filter-bar/FilterBar.vue');
export const YoutubePlayer = () => import(/* webpackChunkName: "youtube-player" */ '@/ui-components/YoutubePlayer.vue');
export const VideoJsPlayer = () =>
	import(/* webpackChunkName: "videojs-player" */ '@/ui-components/players/VideoJsPlayer.vue');
export const TrailerModal = () => import(/* webpackChunkName: "trailer-modal" */ '@/components/TrailerModal.vue');

// Filters
export const FilterProductionCountry = () =>
	import(/* webpackChunkName: "premium-filters" */ '@/components/filter-bar/FilterProductionCountry.vue');
export const FilterRuntime = () =>
	import(/* webpackChunkName: "premium-filters" */ '@/components/filter-bar/FilterRuntime.vue');
export const FilterVoteCount = () =>
	import(/* webpackChunkName: "premium-filters" */ '@/components/filter-bar/FilterVoteCount.vue');

export const UserProfile = () => import('@/pages/UserProfile.vue');
export const RentSignin = () => import(/* webpackChunkName: "rent-signin" */ '@/pages/RentSignin.vue');
export const MarkAsSeenModalContainer = () =>
	import(/* webpackChunkName: "mark-as-seen" */ '@/components/mark-as-seen/MarkAsSeenModalContainer.vue');

export const JWTVLP = () => import(/* webpackChunkName: "JWTVLP" */ '@/pages/landing-pages/jwtv/JWTVLP.vue');
